import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { KeyRound, Mail, ShieldAlert, Eye, EyeOff, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * AdminPasswordDialog — R111.
 *
 * Two modes admins can pick from when a user is locked out or on the phone:
 *   1. `direct` — type a new password, admin sets it directly (never emailed).
 *      Best for phone-support: admin says the temporary password over the
 *      phone and forces a change-on-next-login.
 *   2. `link`   — send the same magic reset-link the invite flow uses.
 *      Best for self-service — admin doesn't need to know the password.
 *
 * When email is misconfigured (EMERGENT_EMAIL_KEY unset), the "link" mode
 * still generates a link server-side and copies it to the admin's
 * clipboard as a fallback.
 */
function generatePassword(len = 14) {
  // Curated alphabet — no ambiguous chars (0/O, 1/l) so admins reading
  // over the phone don't fumble.
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const symbols = "!@#$%&*";
  let out = "";
  for (let i = 0; i < len - 1; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  out += symbols[Math.floor(Math.random() * symbols.length)];
  return out;
}

export function AdminPasswordDialog({ user, onClose }) {
  const [mode, setMode] = useState("link");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [forceChange, setForceChange] = useState(true);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const passwordValid = password.length >= 10
    && /[a-zA-Z]/.test(password) && /[0-9]/.test(password);

  const suggest = () => {
    const p = generatePassword();
    setPassword(p);
    setReveal(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "direct") {
        if (!passwordValid) {
          toast.error("Password needs 10+ chars, one letter, one digit");
          setBusy(false);
          return;
        }
        await api.post(`/admin/users/${user.id}/set-password`, {
          password, force_change_on_login: forceChange,
        });
        toast.success(`Password set for ${user.email}`, {
          duration: 8000,
          description: forceChange
            ? "They'll be prompted to change it on next sign-in."
            : "Share it with them out-of-band (phone, in-person).",
          action: {
            label: "Copy password",
            onClick: () => { navigator.clipboard.writeText(password); toast.info("Password copied"); },
          },
        });
        onClose(true);
      } else {
        const { data } = await api.post(`/admin/users/${user.id}/reset-password`);
        if (data?.delivered) {
          toast.success(`Reset link emailed to ${user.email}`);
        } else {
          const link = data?.reset_link;
          toast.warning(`Couldn't send the reset email`, {
            duration: 12000,
            description: link
              ? `${data?.error || "Email service unavailable"} — copy the fallback link below and share it directly.`
              : (data?.error || "No fallback link available — contact support."),
            action: link ? {
              label: "Copy reset link",
              onClick: () => { navigator.clipboard.writeText(link); toast.info("Link copied"); },
            } : undefined,
          });
        }
        onClose(true);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="bg-card border-white/10 max-w-md" data-testid="admin-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-orange-400" />
            Change password for {user.name}
          </DialogTitle>
          <DialogDescription>
            {user.email} · {user.role}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 mt-2">
          <button
            type="button"
            data-testid="pw-mode-link"
            onClick={() => setMode("link")}
            className={`p-3 rounded-lg border text-left transition ${
              mode === "link"
                ? "border-orange-500/40 bg-orange-500/10"
                : "border-white/10 hover:bg-white/[0.03]"
            }`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Mail className="h-3.5 w-3.5 text-orange-300" /> Email reset link
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Sends a magic link the user opens to set their own password.
            </div>
          </button>
          <button
            type="button"
            data-testid="pw-mode-direct"
            onClick={() => setMode("direct")}
            className={`p-3 rounded-lg border text-left transition ${
              mode === "direct"
                ? "border-orange-500/40 bg-orange-500/10"
                : "border-white/10 hover:bg-white/[0.03]"
            }`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <KeyRound className="h-3.5 w-3.5 text-orange-300" /> Set directly
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Type or generate a password. Share it with them out-of-band.
            </div>
          </button>
        </div>

        {mode === "direct" && (
          <div className="mt-4 space-y-3" data-testid="pw-direct-block">
            <div className="flex items-center gap-2 text-[11px] text-amber-300 bg-amber-500/[0.06] border border-amber-500/20 rounded p-2">
              <ShieldAlert className="h-3.5 w-3.5" />
              This password will be set immediately. Existing sessions will be invalidated.
            </div>
            <div>
              <Label>New password</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    data-testid="pw-input"
                    type={reveal ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-white/[0.04] border-white/10 pr-9"
                    placeholder="At least 10 chars with a letter + digit"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    onClick={() => setReveal(!reveal)}
                    data-testid="pw-reveal">
                    {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="border-white/10 shrink-0"
                  onClick={suggest}
                  data-testid="pw-suggest">
                  Generate
                </Button>
                {password && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="border-white/10 shrink-0"
                    onClick={() => { navigator.clipboard.writeText(password); toast.info("Copied"); }}
                    data-testid="pw-copy">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="mt-2 text-[11px] text-zinc-500">
                {password && !passwordValid && (
                  <span className="text-rose-300">Password needs at least one letter and one digit.</span>
                )}
                {password && passwordValid && (
                  <span className="text-emerald-300">Looks good.</span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between p-2 rounded bg-white/[0.02] border border-white/10">
              <div>
                <div className="text-xs font-medium">Force change on next login</div>
                <div className="text-[11px] text-zinc-500">Prompt them to pick their own on first sign-in.</div>
              </div>
              <Switch checked={forceChange} onCheckedChange={setForceChange} data-testid="pw-force-change" />
            </div>
          </div>
        )}

        {mode === "link" && (
          <div className="mt-4 text-xs text-zinc-400 p-3 rounded bg-white/[0.02] border border-white/10" data-testid="pw-link-block">
            An email will be sent to <b className="text-zinc-200">{user.email}</b> with
            a 14-day set-password link. If the email service is misconfigured
            (production frequently hits this), you&apos;ll get the fallback link
            in the toast so you can share it manually.
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onClose(false)} className="border-white/10" data-testid="pw-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || (mode === "direct" && !passwordValid)}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="pw-submit">
            {busy ? "Working…" : mode === "direct" ? "Set password" : "Send reset link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
